import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { layoutCSS } from "../../common/commonStyle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import WifiIcon from "@mui/icons-material/Wifi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StorageIcon from "@mui/icons-material/Storage";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import * as XLSX from "xlsx";
import { useGetRcscategoryQuery } from "../../../store/services/rcstemplateApi";
import { useGetTrueCallerNumberQuery } from "../../../store/services/trucallerApi";
import { useGetSearchFilesQuery } from "../../../store/services/principleApi";
import {
  useCreateTruecallerMutation,
  useTestTruecallerMutation,
  useGetCampaignbodyQuery,
} from "../../../store/services/trucallerApi";
import { toast } from "react-toastify";

// ─── Theme ────────────────────────────────────────────────────────────────────
const PURPLE = "#7c3aed";
const PURPLE_LIGHT = "#c4b5fd";
const FS = "0.875rem"; // normal font size
const FIELD_H = 36; // one height for every single-line field, so rows line up

const glass = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 2px 10px rgba(124,58,237,0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    borderRadius: "8px",
    fontSize: FS,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d6cdf3",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PURPLE_LIGHT },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PURPLE,
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": { fontSize: FS, color: "#64748B" },
  "& .MuiInputLabel-root.Mui-focused": { color: PURPLE },
  "& .MuiInputBase-input": { fontSize: FS },
  "& .MuiSelect-select": { fontSize: FS },
};

// Single-line text / number / datetime / select: all exactly FIELD_H tall,
// text vertically centred, so mixed fields in one row stay aligned.
const smallSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_H,
  },
  "& .MuiInputBase-input": {
    fontSize: FS,
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
  "& .MuiSelect-select": {
    fontSize: FS,
    py: 0,
    display: "flex",
    alignItems: "center",
    height: "100% !important",
    boxSizing: "border-box",
    minHeight: "unset",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.72rem",
    mx: 0,
    mt: "2px",
  },
};

const menuItemSx = { fontSize: FS, minHeight: 30 };

const switchLabelSx = {
  m: 0,
  "& .MuiFormControlLabel-label": { fontSize: FS },
};

const outlinedPurpleBtn = {
  textTransform: "none",
  fontSize: FS,
  fontWeight: 600,
  height: FIELD_H,
  px: 1.25,
  borderRadius: "8px",
  borderColor: PURPLE_LIGHT,
  color: PURPLE,
  background: "rgba(255,255,255,0.6)",
  justifyContent: "flex-start",
  overflow: "hidden",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: PURPLE, background: "rgba(124,58,237,0.06)" },
};

// Upload / Database buttons: same compact size as the RCS page
const uploadBtnSx = {
  flex: 1,
  minWidth: 0,
  height: FIELD_H,
  px: 1,
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  justifyContent: "flex-start",
  borderRadius: "8px",
  borderColor: PURPLE_LIGHT,
  color: PURPLE,
  background: "rgba(255,255,255,0.7)",
  backdropFilter: "blur(8px)",
  "& .MuiButton-startIcon": { mr: 0.5 },
  "&:hover": {
    borderColor: PURPLE,
    background: "rgba(255,255,255,0.9)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },
};

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
};

const pillBtn = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: FS,
  height: 34,
  px: 2.5,
  borderRadius: "20px",
  background: "#7c6cf6",
  boxShadow: "none",
  "&:hover": { background: PURPLE, boxShadow: "none" },
};

const dialogHeadSx = {
  bgcolor: "#7c6cf6",
  "& th": {
    color: "white",
    fontSize: "0.8rem",
    py: 0.6,
    borderBottom: "none",
  },
};

const dialogCellSx = { fontSize: "0.85rem", py: 0.25, textAlign: "center" };

const SectionCard = ({ title, children, sx }) => (
  <Paper
    elevation={0}
    sx={{ ...glass, p: 1, mb: 0.75, borderRadius: "10px", ...sx }}
  >
    {title && (
      <Typography
        sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", mb: 0.5 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Paper>
);

// ─── Component ────────────────────────────────────────────────────────────────
const TruecallerCampign = ({ service_id }) => {
  const [selectedFile, setSelectedFile] = useState(null);

  const { data: dbFiles, isLoading: dbFilesLoading } = useGetSearchFilesQuery();
  const { data: getCampaignbody } = useGetCampaignbodyQuery();
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [file, setFile] = useState(null);
  const [timeZone, setTimeZone] = useState("Asia/Calcutta");
  const [enableDateTime, setEnableDateTime] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [content, setContent] = useState("");
  const [selectedDbFile, setSelectedDbFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateCategory, setTemplateCaegory] = useState("");
  const [fields, setFields] = useState([]);
  const [fileNames, setFileNames] = useState("");
  const { data: GetTrueCaller } = useGetTrueCallerNumberQuery();
  const fileInputRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [testTruecaller] = useTestTruecallerMutation();
  const [createTruecaller] = useCreateTruecallerMutation();
  const { data: getRcsCategory } = useGetRcscategoryQuery();
  const MAX_ALLOWED_NUMBERS = 1000000;

  // ── Filters (same as RCS) ──
  const [TTL, setTTL] = useState("");
  const [TTLTime, setTTLTime] = useState(false); // false = Days, true = Hours
  const [enableFilterTill, setEnableFilterTill] = useState(false);
  const [filterDatatill_days, setFilterDatatill_days] = useState("");
  const [enableSplit, setEnableSplit] = useState(false);
  const [batchSize, setBatchSize] = useState("");
  const [timeInterval, setTimeInterval] = useState("");
  const [errors, setErrors] = useState({});

  const filteredTemplates = (getCampaignbody?.templates || []).filter(
    (template) =>
      (template.template_name || "")
        .toLowerCase()
        .includes(templateSearch.trim().toLowerCase()),
  );

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const timeZones = Intl.supportedValuesOf
    ? Intl.supportedValuesOf("timeZone")
    : [
        "UTC",
        "America/New_York",
        "Europe/London",
        "Asia/Kolkata",
        "Asia/Tokyo",
        "Asia/Calcutta",
      ];
  const minDateTime = new Date().toISOString().slice(0, 16);

  const handleCheckboxChange = (id) => {
    setSelectedTemplates((prev) => (prev[0] === id ? [] : [id]));
  };

  const handleBatchSizeChange = (e) => {
    const value = e.target.value;
    setBatchSize(value);
    setErrors((prev) => ({
      ...prev,
      batchSize:
        value && Number(value) < 1000 ? "Minimum batch size is 1000" : undefined,
    }));
  };

  const handleTimeIntervalChange = (e) => {
    const value = e.target.value;
    setTimeInterval(value);
    setErrors((prev) => ({
      ...prev,
      timeInterval:
        value && Number(value) < 10 ? "Minimum interval is 10 minutes" : undefined,
    }));
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setFile(null);
      setSelectedDbFile(null);
      setFileNames("");
      return;
    }
    const selectedFile = files[0];
    setSelectedDbFile(null);

    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
    const allowedExtensions = ["csv", "txt", "xlsx"];

    if (!allowedExtensions.includes(fileExtension)) {
      setError("Only .csv, .txt, and .xlsx files are allowed.");
      setFile(null);
      setSelectedDbFile(null);
      setFileNames("");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      let content = e.target.result;

      if (fileExtension === "xlsx") {
        try {
          const workbook = XLSX.read(content, { type: "binary" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          content = XLSX.utils.sheet_to_csv(firstSheet);
        } catch (err) {
          setError("Failed to read XLSX file.");
          setFile(null);
          setSelectedDbFile(null);
          setFileNames("");
          event.target.value = "";
          return;
        }
      }

      const numberArray = content
        .split(/[\n,\s]+/)
        .map((n) => n.trim())
        .filter((n) => /^\d{10,}$/.test(n));

      if (numberArray.length > MAX_ALLOWED_NUMBERS) {
        setError("File contains more than 10 lakh numbers.");
        setFile(null);
        setSelectedDbFile(null);
        setFileNames("");
        event.target.value = "";
        return;
      }

      setSelectedDbFile(null);
      setFile(selectedFile);
      setFileNames(`${selectedFile.name} `);
      setError("");
    };

    if (fileExtension === "xlsx") {
      reader.readAsBinaryString(selectedFile);
    } else {
      reader.readAsText(selectedFile);
    }
  };

  // Filter / schedule fields shared by Test and Submit
  const appendConfigFields = (formData) => {
    formData.append("ttl", TTL || 0);
    formData.append("ttl_time_type", TTLTime ? "hours" : "days");
    formData.append("filterDatatill_days", filterDatatill_days || 0);

    if (enableDateTime && dateTime) {
      formData.append("time_to_send", dateTime);
      formData.append("time_zone", timeZone);
    }

    if (enableSplit) {
      formData.append("batch_size", batchSize || 0);
      formData.append("time_interval", timeInterval || 0);
    }
  };

  const handleTestSubmit = async () => {
    const formData = new FormData();
    formData.append("campaign_name", campaignName);
    formData.append("tcNumber", phoneNumber?.tc_number);
    formData.append("campaign_category", templateCategory);
    formData.append("tc_template_id", selectedTemplates);
    formData.append("contacts", JSON.stringify(contacts));
    appendConfigFields(formData);

    try {
      await testTruecaller(formData).unwrap();
      toast.success("Campaign created successfully!");
    } catch (err) {
      console.error("❌ Error creating campaign:", err);
      toast.error("Failed to create campaign.");
    }
  };

  const handleSubmit = async () => {
    if (enableSplit) {
      const splitErrors = {};
      if (!batchSize || Number(batchSize) < 1000)
        splitErrors.batchSize = "Minimum batch size is 1000";
      if (!timeInterval || Number(timeInterval) < 10)
        splitErrors.timeInterval = "Minimum interval is 10 minutes";
      if (Object.keys(splitErrors).length > 0) {
        setErrors(splitErrors);
        toast.error("Please fix the split scheduler fields");
        return;
      }
    }

    const formData = new FormData();
    formData.append("campaign_name", campaignName);
    formData.append("tcNumber", phoneNumber?.tc_number);
    formData.append("campaign_category", templateCategory);
    formData.append("tc_template_id", selectedTemplates);
    formData.append("contacts", JSON.stringify(contacts));
    appendConfigFields(formData);

    if (selectedDbFile) {
      formData.append("dataFile", selectedDbFile.path);
    } else if (file) {
      formData.append("dataFile", file);
    } else {
      toast.error("Please select a file");
      return;
    }

    try {
      await createTruecaller(formData).unwrap();
      toast.success("Campaign created successfully!");
    } catch (err) {
      toast.error("Failed to create campaign.");
    }
  };

  const hasPreviewContent =
    !!content ||
    !!selectedFile?.preview ||
    (Array.isArray(fields) && fields.length > 0);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ ...layoutCSS, p: 1 }}>
        {/* Title bar */}
        <Paper
          elevation={0}
          sx={{
            ...glass,
            display: "flex",
            alignItems: "center",
            px: 1.5,
            py: 0.6,
            mb: 1,
            borderRadius: "10px",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>
            Create Campaign
          </Typography>
        </Paper>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          {/* ── Left: form panel ── */}
          <Paper
            elevation={0}
            sx={{
              ...glass,
              background: "rgba(255,255,255,0.4)",
              p: 1,
              borderRadius: "14px",
              flex: "1 1 560px",
              minWidth: 0,
            }}
          >
            {/* Campaign details */}
            <SectionCard title="Campaign Details">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                <TextField
                  required
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  size="small"
                  label="Campaign Name"
                  sx={{ ...smallSx, width: 210 }}
                />
                <FormControl required size="small" sx={{ ...smallSx, width: 170 }}>
                  <InputLabel id="tc-number-label">TC.no</InputLabel>
                  <Select
                    labelId="tc-number-label"
                    label="TC.no"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  >
                    <MenuItem value="" disabled sx={menuItemSx}>
                      Select TC.no
                    </MenuItem>
                    {GetTrueCaller?.data?.map((item) => (
                      <MenuItem key={item.tc_number_id} value={item} sx={menuItemSx}>
                        {item.tc_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl required size="small" sx={{ ...smallSx, width: 190 }}>
                  <InputLabel id="template-category-label">
                    Template Category
                  </InputLabel>
                  <Select
                    labelId="template-category-label"
                    label="Template Category"
                    value={templateCategory}
                    onChange={(e) => setTemplateCaegory(e.target.value)}
                  >
                    <MenuItem value="" disabled sx={menuItemSx}>
                      Select Template Category
                    </MenuItem>
                    <MenuItem value="media" sx={menuItemSx}>Media</MenuItem>
                    <MenuItem value="text" sx={menuItemSx}>Text</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </SectionCard>

            {/* Upload & Configure (filters from RCS) */}
            <SectionCard title="Upload & Configure">
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  alignItems: "center",
                }}
              >
                {/* Upload + Database: one small 220px pair, same as RCS */}
                <Box sx={{ display: "flex", gap: 1, width: 220 }}>
                  <Tooltip title={file ? file.name : ""} arrow>
                    <Button
                      component="label"
                      role={undefined}
                      variant="outlined"
                      startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                      sx={uploadBtnSx}
                    >
                      <Box component="span" sx={ellipsisSx}>
                        {file ? file.name : "Upload"}
                      </Box>
                      <input
                        type="file"
                        hidden
                        id="fileUpload"
                        accept=".csv,.txt,.xlsx"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                      />
                    </Button>
                  </Tooltip>

                  <Button
                    variant="outlined"
                    startIcon={<StorageIcon sx={{ fontSize: 18 }} />}
                    onClick={() => setIsFileModalOpen(true)}
                    sx={uploadBtnSx}
                  >
                    <Box component="span" sx={ellipsisSx}>
                      {selectedDbFile ? selectedDbFile.filter_name : "Database"}
                    </Box>
                  </Button>
                </Box>

                {/* TTL */}
                <TextField
                  label="TTL"
                  type="number"
                  size="small"
                  sx={{ ...smallSx, width: 90 }}
                  value={TTL}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || Number(value) >= 0) {
                      setTTL(value);
                    }
                  }}
                  inputProps={{
                    min: 0,
                    onKeyDown: (e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    },
                    onWheel: (e) => {
                      if (Number(TTL) <= 0 && e.deltaY > 0) {
                        e.preventDefault();
                      }
                    },
                  }}
                />

                {/* TTL unit toggle */}
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ height: FIELD_H, whiteSpace: "nowrap" }}
                >
                  <Typography sx={{ fontSize: FS, mr: 0.5 }}>TTL Unit:</Typography>
                  <Switch
                    checked={TTLTime}
                    onChange={(e) => setTTLTime(e.target.checked)}
                    size="small"
                  />
                  <Typography sx={{ fontSize: FS, ml: 0.5 }}>
                    {TTLTime ? "Hours" : "Days"}
                  </Typography>
                </Box>

                {/* Filter From LC: toggle + dropdown always stay on one line */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    gap: 0.75,
                    whiteSpace: "nowrap",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={enableFilterTill}
                        onChange={(e) => {
                          setEnableFilterTill(e.target.checked);
                          if (!e.target.checked) setFilterDatatill_days(0);
                        }}
                      />
                    }
                    label="Filter From LC"
                    sx={{ ...switchLabelSx, height: FIELD_H }}
                  />

                  {enableFilterTill && (
                    <FormControl size="small" sx={{ ...smallSx, width: 140 }}>
                      <InputLabel>Filter Till</InputLabel>
                      <Select
                        value={filterDatatill_days}
                        label="Filter Till"
                        onChange={(e) => setFilterDatatill_days(e.target.value)}
                      >
                        <MenuItem value={1} sx={menuItemSx}>Today</MenuItem>
                        <MenuItem value={2} sx={menuItemSx}>Yesterday</MenuItem>
                        {Array.from({ length: 28 }, (_, i) => (
                          <MenuItem key={i + 3} value={i + 3} sx={menuItemSx}>
                            Last {i + 3} days
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>

              {/* Split scheduler */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  gap: 1,
                  mt: 0.75,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={enableSplit}
                      onChange={(e) => {
                        setEnableSplit(e.target.checked);
                        if (!e.target.checked) {
                          setErrors((prev) => ({
                            ...prev,
                            batchSize: undefined,
                            timeInterval: undefined,
                          }));
                        }
                      }}
                    />
                  }
                  label="Enable Split Scheduler"
                  sx={{ ...switchLabelSx, height: FIELD_H }}
                />
                {enableSplit && (
                  <>
                    <TextField
                      label="Batch Size"
                      type="number"
                      size="small"
                      value={batchSize}
                      onChange={handleBatchSizeChange}
                      error={!!errors.batchSize}
                      helperText={errors.batchSize || "Min 1000"}
                      inputProps={{ min: 1000 }}
                      sx={{ ...smallSx, width: 160 }}
                    />
                    <TextField
                      label="Interval (minutes)"
                      type="number"
                      size="small"
                      value={timeInterval}
                      onChange={handleTimeIntervalChange}
                      error={!!errors.timeInterval}
                      helperText={errors.timeInterval || "Min 10 min"}
                      inputProps={{ min: 10 }}
                      sx={{ ...smallSx, width: 170 }}
                    />
                  </>
                )}
              </Box>

              {error && (
                <Typography sx={{ fontSize: "0.8rem", color: "#dc2626", mt: 0.5 }}>
                  {error}
                </Typography>
              )}
            </SectionCard>

            {/* Select Template + Test side by side */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <SectionCard
                title="Select Template *"
                sx={{ flex: "1 1 340px", mb: 0.75 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    value={templateName}
                    placeholder="No template selected"
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ ...smallSx, flex: 1, minWidth: 170 }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => setIsTemplateModalOpen(true)}
                    sx={{ ...pillBtn, height: 32, px: 2 }}
                  >
                    Browse
                  </Button>

                  {selectedFile && (
                    <Button
                      component="label"
                      role={undefined}
                      variant="outlined"
                      tabIndex={-1}
                      startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                      sx={{ ...outlinedPurpleBtn, maxWidth: 220 }}
                    >
                      <Box
                        component="span"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {selectedFile.file?.name
                          ? selectedFile.file.name
                          : "Change media"}
                      </Box>
                      <input
                        type="file"
                        hidden
                        accept=".svg,.png,.jpg"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            const f = e.target.files[0];
                            setSelectedFile({
                              file: f,
                              preview: URL.createObjectURL(f),
                            });
                          }
                        }}
                      />
                    </Button>
                  )}
                </Box>

                {content && (
                  <TextField
                    label="Message"
                    multiline
                    rows={2}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ ...fieldSx, mt: 0.75 }}
                  />
                )}
              </SectionCard>

              <SectionCard
                title="Test Campaign"
                sx={{ flex: "1 1 280px", mb: 0.75 }}
              >
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                  <TextField
                    label="Mobile Number"
                    size="small"
                    onChange={(e) => {
                      const arr = e.target.value.split(",").map((n) => n.trim());
                      setContacts(arr);
                    }}
                    value={contacts}
                    sx={{ ...smallSx, flex: 1, minWidth: 140 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleTestSubmit}
                    sx={{ ...pillBtn, height: 32, px: 2 }}
                  >
                    Test
                  </Button>
                </Box>
              </SectionCard>
            </Box>

            {/* Schedule: switch + aligned date-time and time zone */}
            <SectionCard title="Schedule Campaign">
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FormControlLabel
                  sx={{ ...switchLabelSx, height: FIELD_H }}
                  control={
                    <Switch
                      size="small"
                      checked={enableDateTime}
                      onChange={(e) => setEnableDateTime(e.target.checked)}
                    />
                  }
                  label="Schedule for later"
                />
                {enableDateTime && (
                  <>
                    <TextField
                      label="Date & Time"
                      type="datetime-local"
                      size="small"
                      value={dateTime}
                      onChange={(e) => setDateTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: minDateTime }}
                      sx={{ ...smallSx, width: 230 }}
                    />
                    <FormControl size="small" sx={{ ...smallSx, width: 230 }}>
                      <InputLabel id="timezone-label" shrink>
                        Time Zone
                      </InputLabel>
                      <Select
                        labelId="timezone-label"
                        value={timeZone}
                        label="Time Zone"
                        notched
                        onChange={(e) => setTimeZone(e.target.value)}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                      >
                        {timeZones.map((tz) => (
                          <MenuItem key={tz} value={tz} sx={menuItemSx}>
                            {tz}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Box>
            </SectionCard>

            {/* Submit / Cancel on the left */}
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Button variant="contained" onClick={handleSubmit} sx={pillBtn}>
                Submit
              </Button>
              <Button
                variant="outlined"
                sx={{ ...outlinedPurpleBtn, borderRadius: "20px", px: 2.5, height: 34 }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>

          {/* ── Right: phone preview ── */}
          <Box sx={{ width: 266, mx: "auto", position: "sticky", top: 8 }}>
            <Box
              sx={{
                width: 260,
                height: 450,
                margin: "0 auto",
                border: "3px solid #0f172a",
                borderRadius: "28px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                background: "#f4f4f5",
                boxShadow: "0 10px 28px rgba(15,23,42,0.25)",
              }}
            >
              {/* Status bar */}
              <Box
                sx={{
                  height: 28,
                  px: 1.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#0f172a",
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fbbf24" }}>
                  {currentTime}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <WifiIcon sx={{ fontSize: 14, color: "#e2e8f0" }} />
                  <SignalCellularAltIcon sx={{ fontSize: 14, color: "#e2e8f0" }} />
                  <BatteryFullIcon sx={{ fontSize: 14, color: "#e2e8f0" }} />
                </Stack>
              </Box>

              {/* Header */}
              <Box
                sx={{
                  height: 40,
                  px: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  background: "#f1f5f9",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18, color: "#cbd5e1" }} />
                <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#0f172a" }}>
                  Business Message
                </Typography>
              </Box>

              {/* Body */}
              <Box
                sx={{
                  flex: 1,
                  p: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  background: "#f4f4f5",
                }}
              >
                {!hasPreviewContent ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "4px",
                        background: "#cbd5e1",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: "#334155",
                        textAlign: "center",
                        lineHeight: 1.5,
                      }}
                    >
                      Select template
                      <br />
                      to preview
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        background: "#fff",
                        p: 0.75,
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        wordWrap: "break-word",
                      }}
                    >
                      {selectedFile?.preview && (
                        <img
                          src={selectedFile.preview}
                          alt="Template Media"
                          style={{
                            width: "100%",
                            borderRadius: "6px",
                            marginBottom: "4px",
                            display: "block",
                          }}
                        />
                      )}
                      {content && (
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            lineHeight: 1.4,
                            whiteSpace: "pre-line",
                            overflowWrap: "break-word",
                            px: 0.5,
                            color: "#0f172a",
                          }}
                        >
                          {content}
                        </Typography>
                      )}
                    </Box>

                    {Array.isArray(fields) &&
                      fields.map((btn, i) => {
                        const label = btn.button_text || btn.quick_reply_text;
                        if (!label) return null;
                        return (
                          <Button
                            key={btn.tc_button_id ?? i}
                            variant="contained"
                            size="small"
                            fullWidth
                            sx={{
                              mt: 0.5,
                              py: 0.3,
                              fontSize: "0.85rem",
                              background: "#fff",
                              color: "#2563eb",
                              border: "1px solid #e2e8f0",
                              textTransform: "none",
                              boxShadow: "none",
                              "&:hover": { background: "#f8fafc", boxShadow: "none" },
                            }}
                            onClick={() => {
                              if (btn.website_URL) {
                                window.open("https://" + btn.website_URL, "_blank");
                              }
                            }}
                          >
                            {label}
                          </Button>
                        );
                      })}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Template dialog ── */}
      {isTemplateModalOpen && (
        <Dialog
          open={isTemplateModalOpen}
          onClose={() => {
            setIsTemplateModalOpen(false);
            setTemplateSearch("");
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, py: 0.75 }}>
            Select Template
          </DialogTitle>
          <DialogContent sx={{ pt: "6px !important", pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Search template by name"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              sx={{ ...smallSx, mb: 0.75 }}
            />
            <TableContainer sx={{ borderRadius: "8px", border: "1px solid #e5defa" }}>
              <Table size="small">
                <TableHead sx={dialogHeadSx}>
                  <TableRow>
                    <TableCell>Sr.No</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>Template Name</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>Template Text</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>Template Type</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.map((template, index) => (
                    <TableRow key={template.tc_template_id}>
                      <TableCell sx={{ fontSize: "0.85rem", py: 0.25 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={dialogCellSx}>{template.template_name}</TableCell>
                      <TableCell sx={dialogCellSx}>{template.template_text}</TableCell>
                      <TableCell sx={dialogCellSx}>
                        {template.template_type_category?.trim()}
                      </TableCell>
                      <TableCell sx={dialogCellSx}>
                        <Checkbox
                          size="small"
                          sx={{ p: 0.25 }}
                          checked={selectedTemplates.includes(template.tc_template_id)}
                          onChange={() => handleCheckboxChange(template.tc_template_id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredTemplates.length === 0 && (
              <Typography sx={{ py: 1, textAlign: "center", fontSize: "0.85rem" }}>
                {templateSearch
                  ? "No templates match your search"
                  : "No templates available"}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
              <Button
                variant="contained"
                sx={pillBtn}
                onClick={() => {
                  const selectedId = selectedTemplates[0];

                  const foundTemplate = getCampaignbody?.templates?.find(
                    (t) => t.tc_template_id === selectedId,
                  );

                  if (foundTemplate) {
                    setContent(foundTemplate.template_text);
                    setTemplateName(foundTemplate.template_name);
                    setFields(foundTemplate.buttons || []);
                  }
                  if (foundTemplate?.media_file_path) {
                    setSelectedFile({
                      file: null,
                      preview: `http://192.168.1.34:9001/api/${foundTemplate.media_file_path}`,
                    });
                  }

                  setIsTemplateModalOpen(false);
                  setTemplateSearch("");
                }}
              >
                Submit
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Database file dialog ── */}
      {isFileModalOpen && (
        <Dialog
          open={isFileModalOpen}
          onClose={() => setIsFileModalOpen(false)}
          maxWidth={false}
          sx={{
            "& .MuiDialog-paper": {
              width: { xs: "95vw", sm: "60vw", md: "50vw" },
              maxHeight: "80vh",
              borderRadius: "12px",
            },
          }}
        >
          <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, py: 0.75 }}>
            Select File From Database
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            {dbFilesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : dbFiles?.data?.length > 0 ? (
              <Box sx={{ width: "100%" }}>
                <TableContainer sx={{ borderRadius: "8px", border: "1px solid #e5defa" }}>
                  <Table size="small">
                    <TableHead sx={dialogHeadSx}>
                      <TableRow>
                        <TableCell>Sr.No</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Filter Name</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>File No</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Total Count</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Fields In File</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Created At</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Select</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dbFiles.data.map((f, index) => (
                        <TableRow key={f.path}>
                          <TableCell sx={{ fontSize: "0.85rem", py: 0.25 }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={dialogCellSx}>{f.filter_name}</TableCell>
                          <TableCell sx={dialogCellSx}>{f.page_no}</TableCell>
                          <TableCell sx={dialogCellSx}>{f.totalcount}</TableCell>
                          <TableCell sx={dialogCellSx}>{f.fieldsinfile}</TableCell>
                          <TableCell sx={dialogCellSx}>
                            {dayjs(f.createdat).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell sx={dialogCellSx}>
                            <Radio
                              size="small"
                              sx={{ p: 0.25 }}
                              checked={selectedDbFile?.path === f.path}
                              onChange={() => setSelectedDbFile(f)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {selectedDbFile && (
                  <Box sx={{ mt: 0.75, textAlign: "right" }}>
                    <Button
                      variant="contained"
                      sx={pillBtn}
                      onClick={() => {
                        setFile(null);
                        setFileNames(`From DB: ${selectedDbFile.filter_name}`);
                        setIsFileModalOpen(false);
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontSize: "0.875rem" }}>
                No files available in database
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default TruecallerCampign;